import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Loader2, Database, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface CacheRow {
  airline_code: string;
  earliest_date: string;
  latest_date: string;
  total_rows: number;
  days_coverage: number;
}

interface CacheStatusProps {
  refreshKey: number;
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function formatDateStr(d: Date): string {
  return d.toISOString().split("T")[0];
}

function toUtcDatetime(dateStr: string, timeStr: string): string {
  const [h, m] = timeStr.split(":").map(Number);
  const dt = new Date(`${dateStr}T${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:00+10:00`);
  return dt.toISOString();
}

export default function CacheStatus({ refreshKey }: CacheStatusProps) {
  const [rows, setRows] = useState<CacheRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchCacheStatus = useCallback(async () => {
    setLoading(true);

    const { data, error } = await supabase
      .from("flight_daily_cache")
      .select("dep_date, pattern_id");

    if (error) {
      console.error("Failed to fetch cache:", error.message);
      setLoading(false);
      return;
    }

    // We need airline_code from patterns. Fetch pattern -> airline mapping.
    const { data: patterns } = await supabase
      .from("flight_patterns")
      .select("id, airline_code");

    const patternAirlineMap = new Map<number, string>();
    for (const p of patterns || []) {
      patternAirlineMap.set(p.id, p.airline_code);
    }

    // Group cache rows by airline
    const airlineMap = new Map<string, { dates: Set<string>; count: number; min: string; max: string }>();
    for (const c of data || []) {
      const airline = patternAirlineMap.get(c.pattern_id as number) || "Unknown";
      let entry = airlineMap.get(airline);
      if (!entry) {
        entry = { dates: new Set(), count: 0, min: c.dep_date, max: c.dep_date };
        airlineMap.set(airline, entry);
      }
      entry.dates.add(c.dep_date);
      entry.count++;
      if (c.dep_date < entry.min) entry.min = c.dep_date;
      if (c.dep_date > entry.max) entry.max = c.dep_date;
    }

    const result: CacheRow[] = Array.from(airlineMap.entries()).map(([airline, e]) => ({
      airline_code: airline,
      earliest_date: e.min,
      latest_date: e.max,
      total_rows: e.count,
      days_coverage: e.dates.size,
    }));

    setRows(result.sort((a, b) => a.airline_code.localeCompare(b.airline_code)));
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchCacheStatus();
  }, [fetchCacheStatus, refreshKey]);

  const getStatus = (row: CacheRow) => {
    if (row.total_rows === 0) return { label: "Empty", color: "🔴" };
    const today = new Date();
    const latest = new Date(row.latest_date + "T00:00:00");
    const diffDays = Math.ceil((latest.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays > 14) return { label: "Healthy", color: "🟢" };
    return { label: "Low", color: "🟡" };
  };

  const handleRefreshCache = async (airlineCode: string) => {
    setRefreshing(airlineCode);

    try {
      // Fetch all patterns for this airline
      const { data: patterns, error: pErr } = await supabase
        .from("flight_patterns")
        .select("id, flight_number, dep_airport, dep_time, arr_airport, arr_time, days_bitmask, aircraft_type, notes");

      if (pErr) throw new Error(pErr.message);

      // Filter to this airline's patterns via a second query
      const { data: airlinePatterns, error: apErr } = await supabase
        .from("flight_patterns")
        .select("id, flight_number, dep_airport, dep_time, arr_airport, arr_time, days_bitmask, aircraft_type, notes")
        .eq("airline_code", airlineCode);

      if (apErr) throw new Error(apErr.message);
      if (!airlinePatterns || airlinePatterns.length === 0) {
        toast({ title: "No patterns", description: `No flight patterns found for ${airlineCode}.`, variant: "destructive" });
        setRefreshing(null);
        return;
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const rangeStart = addDays(today, -14);
      const rangeEnd = addDays(today, 30);
      const totalDays = Math.ceil((rangeEnd.getTime() - rangeStart.getTime()) / (1000 * 60 * 60 * 24));

      // Delete existing cache for these patterns in range
      const patternIds = airlinePatterns.map((p) => p.id);
      await supabase
        .from("flight_daily_cache")
        .delete()
        .gte("dep_date", formatDateStr(rangeStart))
        .lte("dep_date", formatDateStr(rangeEnd))
        .in("pattern_id", patternIds as any);

      // Generate cache rows
      const cacheRows: any[] = [];
      for (const pattern of airlinePatterns) {
        for (let d = 0; d <= totalDays; d++) {
          const currentDate = addDays(rangeStart, d);
          const dayBit = currentDate.getDay();
          if (!(pattern.days_bitmask & (1 << dayBit))) continue;

          const depDateStr = formatDateStr(currentDate);
          const depTime = pattern.dep_time as string;
          const arrTime = pattern.arr_time as string;
          const isOvernight = arrTime < depTime;
          const arrDate = isOvernight ? addDays(currentDate, 1) : currentDate;
          const arrDateStr = formatDateStr(arrDate);

          cacheRows.push({
            pattern_id: pattern.id,
            flight_number: pattern.flight_number,
            dep_airport: pattern.dep_airport,
            arr_airport: pattern.arr_airport,
            dep_date: depDateStr,
            dep_datetime: toUtcDatetime(depDateStr, depTime),
            arr_date: arrDateStr,
            arr_datetime: toUtcDatetime(arrDateStr, arrTime),
            aircraft_type: pattern.aircraft_type,
            notes: pattern.notes,
            is_overnight: isOvernight,
          });
        }
      }

      // Bulk insert in batches
      const BATCH = 50;
      for (let i = 0; i < cacheRows.length; i += BATCH) {
        const batch = cacheRows.slice(i, i + BATCH);
        const { error: insErr } = await supabase.from("flight_daily_cache").insert(batch as any);
        if (insErr) throw new Error(insErr.message);
      }

      toast({ title: "Cache refreshed", description: `✅ Cache refreshed — ${cacheRows.length} rows generated for ${airlineCode}` });
      fetchCacheStatus();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      toast({ title: "Cache refresh failed", description: msg, variant: "destructive" });
    } finally {
      setRefreshing(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="h-5 w-5" />
          Cache Status
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : rows.length === 0 ? (
          <p className="text-muted-foreground text-sm text-center py-6">No cache data found.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Airline</TableHead>
                <TableHead>Earliest Cached Date</TableHead>
                <TableHead>Latest Cached Date</TableHead>
                <TableHead>Total Cache Rows</TableHead>
                <TableHead>Days Coverage</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const status = getStatus(row);
                return (
                  <TableRow key={row.airline_code}>
                    <TableCell className="font-mono font-medium">{row.airline_code}</TableCell>
                    <TableCell>{row.earliest_date}</TableCell>
                    <TableCell>{row.latest_date}</TableCell>
                    <TableCell>{row.total_rows.toLocaleString()}</TableCell>
                    <TableCell>{row.days_coverage}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {status.color} {status.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={refreshing === row.airline_code}
                        onClick={() => handleRefreshCache(row.airline_code)}
                        className="gap-1"
                      >
                        {refreshing === row.airline_code ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <RefreshCw className="h-3.5 w-3.5" />
                        )}
                        Refresh
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
